import React from 'react';
import RsSlider from '@Pages/RetroSynthesis/components/RsSlider';
import PropTypes from 'prop-types';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import {
  getOptionsByName,
  OptionSwitch,
  getCheckedValue,
  getOptionByValue,
  PopupRow,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersUtils';
import { SingleSelect } from '@els/els-react--select';
import isEmpty from 'lodash/isEmpty';
import { PUBLISHED_PARAMS_NUMBER_OF_ROUTES_LABEL } from './constants';
import HeaderInfoIcon from '../ProjectRoutes/components/HeaderInfoIcon';
import HeaderInfoTooltip from '../ProjectRoutes/components/HeaderInfoTooltip';

import './PublishedParameters.scss';

const RX2_AUTOPLAN_MAX_BEST_PATHS = 'RX2_AUTOPLAN_MAX_BEST_PATHS';
const RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES = 'RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES';
const RX2_AUTOPLAN_MAX_DEPTH = 'RX2_AUTOPLAN_MAX_DEPTH';
const RX2_AUTOPLAN_STOP_WHEN_COMMERCIALLY_AVAILABLE = 'RX2_AUTOPLAN_STOP_WHEN_COMMERCIALLY_AVAILABLE';
const RX2_AUTOPLAN_DEFAULT_YIELD = 'RX2_AUTOPLAN_DEFAULT_YIELD';
const RX2_AUTOPLAN_FULL_ROUTES = 'RX2_AUTOPLAN_FULL_ROUTES';

export const extractDefaultPublishedParameters = (resp) => ({
  numberOfPlans: getCheckedValue(RX2_AUTOPLAN_MAX_BEST_PATHS, resp),
  maxBranch: getCheckedValue(RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES, resp),
  maxDepth: getCheckedValue(RX2_AUTOPLAN_MAX_DEPTH, resp),
  stopCommand: getCheckedValue(RX2_AUTOPLAN_STOP_WHEN_COMMERCIALLY_AVAILABLE, resp) === OptionSwitch.ON,
  defaultYield: parseInt(getCheckedValue(RX2_AUTOPLAN_DEFAULT_YIELD, resp), 10),
  fullSynthesisPlan: getCheckedValue(RX2_AUTOPLAN_FULL_ROUTES, resp) === OptionSwitch.ON,
});

function PublishedParameters({ optionResponse, onParamsChanged, projectParams, editable }) {
  if (isEmpty(optionResponse) || isEmpty(projectParams)) {
    return null;
  }

  return (
    <>
      {editable && (
        <>
          <PopupRow className="rs-column-subheader mgt">
            Length & depth of synthesis plans
            <HeaderInfoIcon
              tooltipId="info-tooltip-published-length"
              e2eName="info-tooltip-published-length"
              ariaLabel={PUBLISHED_PARAMS_NUMBER_OF_ROUTES_LABEL}
              informationIconTitle={PUBLISHED_PARAMS_NUMBER_OF_ROUTES_LABEL}
            />
            <HeaderInfoTooltip id="info-tooltip-published-length">
              <div className="rs-tooltip-big__content">
                <ul className="info-tooltip-content-list">
                  <li className="info-tooltip-content-list-item bullet-dot">
                    {/* eslint-disable-next-line react/no-unescaped-entities */}
                    The option "Full route" in published retrosynthesis will create routes to the target molecule by searching the Reaxys
                    reaction content from published literature. You have the ability to select the maximum No of routes to be generated
                    between 1 to 10 by using the drop down. The default is 5. The length of the various routes is determined by the stop
                    criterions selected below.
                  </li>
                  <li className="info-tooltip-content-list-item bullet-dot">
                    {/* eslint-disable-next-line react/no-unescaped-entities */}
                    The option "Last step only" will find and display up to 100 preparations from Reaxys reaction content. When looking at
                    them in tree views you can further expand the routes by adding additional steps.
                  </li>
                </ul>
              </div>
            </HeaderInfoTooltip>
          </PopupRow>
          <PopupRow>
            <RadioButtonGroup.Option
              e2eName="checkbox-fullSynthesisPlan-yes"
              checked={projectParams.fullSynthesisPlan}
              label={<span>Full routes: </span>}
              onChange={(selected) => {
                if (selected) {
                  onParamsChanged({ fullSynthesisPlan: true });
                }
              }}
            />
            <SingleSelect
              label="Number of routes"
              shouldHideLabel
              className="single-select-options u-els-margin-1o2"
              size="small"
              options={getOptionsByName(RX2_AUTOPLAN_MAX_BEST_PATHS, optionResponse)}
              selectedOptions={[
                getOptionByValue(RX2_AUTOPLAN_MAX_BEST_PATHS, (projectParams.numberOfPlans || '').toString(), optionResponse)?.value || '',
              ]}
              optionsFlyoutProps={{ noInternalPadding: true, theme: 'default', placement: 'bottom', pointer: true, pointerType: 'primary' }}
              onSelect={(option) => {
                const currentNumberOfPlans = option && parseInt(option.value, 10);
                onParamsChanged({ numberOfPlans: currentNumberOfPlans });
              }}
              required
            />
          </PopupRow>
          <PopupRow className="mgt">
            <RadioButtonGroup.Option
              e2eName="checkbox-fullSynthesisPlan-no"
              checked={!projectParams.fullSynthesisPlan}
              label={<span>Last step only</span>}
              onChange={(selected) => {
                if (selected) {
                  onParamsChanged({ fullSynthesisPlan: false });
                }
              }}
            />
          </PopupRow>
          <hr className="grey-line" />
          <PopupRow className="nmgt">
            Branches per step:
            <SingleSelect
              label="Branches per step"
              shouldHideLabel
              size="small"
              className="single-select-options u-els-margin-1o2"
              options={getOptionsByName(RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES, optionResponse)}
              selectedOptions={[
                getOptionByValue(RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES, (projectParams.maxBranch || '').toString(), optionResponse)
                  ?.value || '',
              ]}
              optionsFlyoutProps={{ noInternalPadding: true, theme: 'default', placement: 'bottom', pointer: true, pointerType: 'primary' }}
              onSelect={(option) => {
                const maxBranch = (option && parseInt(option.value, 10)) || 5;
                onParamsChanged({ maxBranch });
              }}
              required
            />
          </PopupRow>
          <PopupRow>
            Max. number of steps:
            <SingleSelect
              label="Max. number of steps"
              className="single-select-options u-els-margin-1o2"
              shouldHideLabel
              size="small"
              options={getOptionsByName(RX2_AUTOPLAN_MAX_DEPTH, optionResponse)}
              selectedOptions={[
                getOptionByValue(RX2_AUTOPLAN_MAX_DEPTH, (projectParams.maxDepth || '').toString(), optionResponse)?.value || '',
              ]}
              optionsFlyoutProps={{ noInternalPadding: true, theme: 'default', placement: 'bottom', pointer: true, pointerType: 'primary' }}
              onSelect={(option) => {
                const maxDepth = (option && parseInt(option.value, 10)) || 5;
                onParamsChanged({ maxDepth });
              }}
              required
            />
          </PopupRow>
          <PopupRow className="u-els-display-inline-block mgt">
            <span className="nohyphens">
              Stop searching if building block is
              <br /> commercially available
            </span>
            <div className="u-els-display-inline-block u-els-margin-left-1x">
              <RadioButtonGroup.Option
                className="checkbox_inline u-els-margin-left-1o2 u-els-padding-left-none"
                e2eName="checkbox-stopCommand-yes"
                checked={!!projectParams.stopCommand}
                label={<span>Yes</span>}
                onChange={(selected) => {
                  if (selected) {
                    onParamsChanged({ stopCommand: true });
                  }
                }}
              />
              <RadioButtonGroup.Option
                className="checkbox_inline u-els-margin-left-1o2 u-els-padding-left-none"
                e2eName="checkbox-stopCommand-no"
                checked={!projectParams.stopCommand}
                label={<span>No</span>}
                onChange={(selected) => {
                  if (selected) {
                    onParamsChanged({ stopCommand: false });
                  }
                }}
              />
            </div>
          </PopupRow>
          <hr className="grey-line" />
          <PopupRow>Assumed yield for reactions without a given yield</PopupRow>
          <PopupRow>
            <RsSlider
              ariaLabel="Slider to set the assumed yield for reactions without a given yield"
              value={projectParams.defaultYield}
              onChange={(newYield) => {
                const defaultYield = Math.round(newYield);
                onParamsChanged({ defaultYield });
              }}
              min={0}
              max={100}
              step={10}
            />
          </PopupRow>
        </>
      )}
      {!editable && (
        <div className="rs-readonly-section">
          <div className="rs-readonly-row numberOfPlans">
            {projectParams.fullSynthesisPlan ? (
              <span className="rs-readonly-row__label">
                <b>{projectParams.numberOfPlans}</b> full routes (up to)
              </span>
            ) : (
              <span className="rs-readonly-row__label">Last step only</span>
            )}
          </div>
          <div className="rs-readonly-row">
            <span className="rs-readonly-row__label">
              <b>{projectParams.maxBranch}</b> branches per step (up to)
            </span>
          </div>
          <div className="rs-readonly-row">
            <span className="rs-readonly-row__label">
              <b>{projectParams.maxDepth}</b> steps per route (up to)
            </span>
          </div>
          <div className="rs-readonly-row">
            <span className="rs-readonly-row__label">
              <b>{!projectParams.stopCommand ? "Don't Stop" : 'Stop'}</b> at commercial building blocks
            </span>
          </div>
          <div className="rs-readonly-row">
            <span className="rs-readonly-row__label">
              <b>{projectParams.defaultYield}%</b> yield per step (assumed, if not published)
            </span>
          </div>
        </div>
      )}
    </>
  );
}

PublishedParameters.defaultProps = {
  onParamsChanged: () => null,
};

PublishedParameters.propTypes = {
  optionResponse: PropTypes.array,
  onParamsChanged: PropTypes.func,
  projectParams: PropTypes.object,
  editable: PropTypes.bool,
};

export default PublishedParameters;
